import {
  ErrorMessage,
  Form,
  FormDatePicker,
  FormInput,
  FormLabelWrapper
} from "components/common/Form";
import KeyboardContainer from "components/common/Keyboard/Keyboard.container";
import format from "date-fns/format";
import toDate from "date-fns/toDate";
import { isEmpty } from "lodash";
import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState
} from "react";
import { Col, Row } from "react-grid-system";
import {
  SkipForNowButton,
  SubmitButton
} from "screens/Onboarding/SignUp/CustomerPersonalInfo/CustomerPersonalInfoForm.style";

export const testIds = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  zipcode: "zipcode",
  birthday: "birthday",
  keyboard: "keyboard-main"
};

interface IUserProfileFields {
  firstName: string;
  lastName: string;
  email: string;
  zipcode: string;
  birthday: string;
}

interface IProps {
  inputFields: IUserProfileFields;
  error?: string;
  submitButtonText?: string;
  loading?: boolean;
  completed?: boolean;
  showCompleteLater?: boolean;
  removeFirstLastname?: boolean;
  onChange(value: string, name?: string);
  onSubmit(): Promise<any>;
  onFocusIn(event: SyntheticEvent<HTMLInputElement>): void;
}

const CustomerPersonalInfoForm: FunctionComponent<IProps> = ({
  inputFields,
  onFocusIn,
  error,
  loading,
  onChange,
  onSubmit,
  showCompleteLater,
  completed,
  submitButtonText = "Submit",
  removeFirstLastname
}) => {
  const keyboardContainer = KeyboardContainer.useContainer();
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(completed);
  const enable = () => setCanSubmit(true);
  const disable = () => setCanSubmit(false);

  const { setIsOpen, setInputName } = KeyboardContainer.useContainer();

  const handleBirthdayChange = (date: Date) => {
    setSubmitted(false);
    setIsOpen(false);
    onChange(format(date, "MM/dd/yyyy"));
  };

  const onBirthdayInputClick = () => {
    setSubmitted(false);
  };

  const handleOnSubmit = async () => {
    await onSubmit();
    if (!error && !loading) {
      setSubmitted(true);
    }
  };

  const onBirthdayFocus = () => {
    setSubmitted(false);
    setIsOpen(false);
    setInputName("birthday");
  };

  const onBirthdayClearFocus = () => {
    setSubmitted(false);
    setIsOpen(false);
    setInputName("birthday");
  };

  const handleClearDatePicker = () => {
    setSubmitted(false);
    onChange("", "birthday");
  };

  const handleOnFocusIn = (event: SyntheticEvent<HTMLInputElement>) => {
    setSubmitted(false);
    onFocusIn(event);
  };

  const parsedString = isEmpty(inputFields.birthday)
    ? null
    : toDate(Date.parse(inputFields.birthday));

  useEffect(() => {
    keyboardContainer.onInputFieldFocus("firstName");
  }, []);

  return (
    <>
      <Form onValidSubmit={handleOnSubmit} onValid={enable} onInvalid={disable}>
        <>
          {!removeFirstLastname && (
            <Row>
              <Col>
                <FormLabelWrapper block noMargin>
                  First Name
                </FormLabelWrapper>
                <FormInput
                  name="firstName"
                  data-testid={testIds.firstName}
                  value={inputFields.firstName}
                  onFocus={handleOnFocusIn}
                />
              </Col>
              <Col>
                <FormLabelWrapper block noMargin>
                  Last Name
                </FormLabelWrapper>
                <FormInput
                  name="lastName"
                  data-testid={testIds.lastName}
                  value={inputFields.lastName}
                  onFocus={handleOnFocusIn}
                />
              </Col>
            </Row>
          )}
        </>
        <Row>
          <Col>
            <FormLabelWrapper block noMargin>
              Birthday
            </FormLabelWrapper>
            <FormDatePicker
              onInputClick={onBirthdayInputClick}
              onChange={handleBirthdayChange}
              selected={parsedString}
              onFocus={onBirthdayFocus}
              onClearButtonFocus={onBirthdayClearFocus}
              onClearButtonClick={handleClearDatePicker}
            />
          </Col>
          <Col>
            <FormLabelWrapper block noMargin>
              Zip Code
            </FormLabelWrapper>
            <FormInput
              name="zipcode"
              validations={{
                matchRegexp: /\d{5}/
              }}
              validationError="Please enter a zip code with 5 digits."
              data-testid={testIds.zipcode}
              value={inputFields.zipcode}
              onFocus={handleOnFocusIn}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <FormLabelWrapper block noMargin>
              Email Address
            </FormLabelWrapper>
            <FormInput
              name="email"
              validations="isEmail"
              validationError="Please enter a valid email address"
              data-testid={testIds.email}
              value={inputFields.email}
              onFocus={handleOnFocusIn}
            />
          </Col>

          <Col>
            <FormLabelWrapper block noMargin />
            <SubmitButton
              tertiary
              type="submit"
              fullWidth
              completed={submitted}
              disabled={!canSubmit}
              loading={loading}
            >
              {submitButtonText}
            </SubmitButton>
            <ErrorMessage error={error} />
          </Col>
        </Row>
        <>
          {showCompleteLater ? (
            <Row justify={"center"}>
              <SkipForNowButton link disabled={!canSubmit} type="submit">
                Complete Later
              </SkipForNowButton>
            </Row>
          ) : (
            <></>
          )}
        </>
      </Form>
    </>
  );
};

export default CustomerPersonalInfoForm;



// WEBPACK FOOTER //
// ./src/screens/Onboarding/SignUp/CustomerPersonalInfo/CustomerPersonalInfoForm.tsx